import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Stethoscope, MessageSquarePlus, Circle } from "lucide-react";

export function OnlineDoctors({ onConsultationCreated }) {
    const [doctors, setDoctors] = useState([]);
    const [picked, setPicked] = useState(null);
    const [problem, setProblem] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        try {
            const { data } = await api.get("/doctors/online");
            setDoctors(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load doctors");
        }
    };
    useEffect(() => { load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, []);

    const submit = async () => {
        if (!picked || !problem.trim()) { toast.error("Please pick a doctor and describe your problem."); return; }
        setLoading(true);
        try {
            const { data } = await api.post("/consultations", { doctor_id: picked.id, problem });
            toast.success(`Consult started with ${picked.name}. Share PIN ${data.access_pin}.`);
            setProblem("");
            setPicked(null);
            onConsultationCreated && onConsultationCreated(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start consult");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6" data-testid="online-doctors">
            <div className="flex items-center justify-between">
                <h2 className="font-display text-2xl font-semibold">Online doctors</h2>
                <span className="text-xs font-mono text-gray-500">{doctors.filter((d) => d.online).length} of {doctors.length} online</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {doctors.length === 0 && (
                    <div className="md:col-span-2 card-soft p-8 text-center text-sm text-gray-500">
                        No doctors registered yet. They'll appear here as soon as the first one signs up.
                    </div>
                )}
                {doctors.map((d) => {
                    const isPicked = picked?.id === d.id;
                    return (
                        <button
                            key={d.id}
                            onClick={() => setPicked(d)}
                            className={`text-left card-soft p-5 transition-all ${isPicked ? "ring-2 ring-[var(--medx-red)] -translate-y-0.5" : ""}`}
                            data-testid={`doctor-card-${d.id}`}
                        >
                            <div className="flex items-start gap-3">
                                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[var(--medx-red)] to-[var(--medx-orange)] grid place-items-center text-white font-bold shrink-0">
                                    {(d.first_name || d.name || "D").slice(0, 1).toUpperCase()}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="flex items-center gap-2">
                                        <div className="font-display font-semibold text-gray-900 truncate">{d.name}</div>
                                        <Circle className={`w-2 h-2 fill-current ${d.online ? "text-green-500" : "text-gray-300"}`} />
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">{d.speciality}</div>
                                    {d.hospital && <div className="text-[10px] text-gray-400 truncate">{d.hospital}</div>}
                                    {d.bio && <div className="text-xs text-gray-600 mt-2 line-clamp-2">{d.bio}</div>}
                                </div>
                            </div>
                        </button>
                    );
                })}
            </div>

            <div className="card-soft p-6 space-y-3">
                <div className="text-xs font-mono tracking-[0.22em] text-gray-500 flex items-center gap-2">
                    <Stethoscope className="w-3 h-3 text-[var(--medx-orange)]" /> START A CONSULT
                </div>
                <div className="text-sm text-gray-700">
                        {!picked ? <span>Pick a doctor above first.</span> : <>Consulting with <strong>{picked.name}</strong> ({picked.speciality})</>}
                </div>
                <textarea rows={4} value={problem} onChange={(e) => setProblem(e.target.value)}
                    placeholder="What's bothering you? (e.g. persistent headaches for 3 days, slight fever, blurred vision)"
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[var(--medx-orange)] outline-none text-sm resize-none"
                    disabled={!picked}
                    data-testid="consult-problem" />
                <button onClick={submit} disabled={!picked || loading || !problem.trim()} className="btn-primary inline-flex items-center gap-2 disabled:opacity-50" data-testid="consult-start-btn">
                    <MessageSquarePlus className="w-4 h-4" /> {loading ? "Starting…" : "Start consult"}
                </button>
                <p className="text-xs text-gray-400">A 4-digit PIN will be created. Share it with the doctor so they can unlock your vitals.</p>
            </div>
        </div>
    );
}
